import React, { useState, useEffect } from "react";
import axios from "axios";
import { Icon } from "../Icon";

interface ApiSettingsProps {
  user: {
    id: string;
    username: string;
    userType: "user" | "premium" | "vip" | "admin";
    approved?: boolean;
  };
  authToken: string;
}

const ApiSettings: React.FC<ApiSettingsProps> = ({ user, authToken }) => {
  const [apiToken, setApiToken] = useState<string | null>(null);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    if (
      user.userType === "premium" ||
      user.userType === "vip" ||
      user.userType === "admin"
    ) {
      fetchApiToken();
    }
  }, [user.userType]);

  const fetchApiToken = async () => {
    try {
      const response = await axios.get("/api/user/api-token", {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.token) {
        setApiToken(response.data.token);
      }
    } catch (error: any) {
      // If it's a 403 (forbidden), that's expected for free users
      if (error.response?.status !== 403) {
        console.error("Error fetching API token:", error);
      }
    }
  };

  const generateApiToken = async () => {
    // If token exists, warn user about regeneration
    if (apiToken) {
      const confirmed = window.confirm(
        "Warning: Regenerating your API token will invalidate the current token.\n\n" +
          "All applications and services using the current token will need to be updated with the new token.\n\n" +
          "Are you sure you want to regenerate your API token?",
      );

      if (!confirmed) {
        return;
      }
    }

    setGeneratingToken(true);
    try {
      const response = await axios.post(
        "/api/user/api-token/generate",
        {},
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setApiToken(response.data.token);
    } catch (error: any) {
      console.error("Error generating API token:", error);
      if (error.response?.status === 403) {
        alert(
          "API access is a premium feature. Please upgrade to Premium to use this feature.",
        );
      } else {
        alert("Failed to generate API token. Please try again.");
      }
    } finally {
      setGeneratingToken(false);
    }
  };

  const copyApiToken = async () => {
    if (apiToken) {
      try {
        await navigator.clipboard.writeText(apiToken);
        setTokenCopied(true);
        setTimeout(() => setTokenCopied(false), 2000);
      } catch (error) {
        console.error("Error copying token:", error);
        alert("Failed to copy token. Please copy it manually.");
      }
    }
  };

  const isPremium =
    user.userType === "premium" ||
    user.userType === "vip" ||
    user.userType === "admin";

  return (
    <div style={{ maxWidth: "800px" }}>
      <h3 style={{ fontSize: "20px", margin: "0 0 24px 0", color: "#e2e8f0" }}>
        API Access
      </h3>

      {!isPremium ? (
        // Free User - Show upgrade prompt
        <div
          style={{
            backgroundColor: "#2d3748",
            padding: "32px",
            borderRadius: "8px",
            border: "1px solid #4a5568",
            textAlign: "center",
          }}
        >
          <div style={{ marginBottom: "16px" }}>
            <Icon name="lock" size={48} />
          </div>
          <h4
            style={{ color: "#9146FF", margin: "0 0 16px 0", fontSize: "20px" }}
          >
            Premium Subscription Required
          </h4>
          <p
            style={{
              color: "#cbd5e0",
              marginBottom: "24px",
              lineHeight: "1.6",
              maxWidth: "500px",
              margin: "0 auto 24px",
            }}
          >
            API access and custom real-time data ingestion features are
            available exclusively for Premium users.
          </p>

          <div
            style={{
              backgroundColor: "#1a202c",
              padding: "20px",
              borderRadius: "8px",
              marginBottom: "24px",
              maxWidth: "500px",
              margin: "0 auto 24px",
            }}
          >
            <h5
              style={{
                color: "#e2e8f0",
                margin: "0 0 12px 0",
                fontSize: "16px",
              }}
            >
              What you can do with API access:
            </h5>
            <ul
              style={{
                color: "#cbd5e0",
                fontSize: "14px",
                textAlign: "left",
                margin: "0",
                paddingLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>Integrate HAL with your custom applications</li>
              <li>
                Send real-time data to your bot (current song, song bpm, game
                info, etc.)
              </li>
              <li>
                HAL understands and uses your custom data in conversations
              </li>
              <li>
                Build custom dashboards and analytics with internal metrics
              </li>
            </ul>
          </div>

          {user.approved !== false ? (
            <button
              onClick={() => (window.location.href = "/dashboard/account")}
              style={{
                padding: "14px 32px",
                backgroundColor: "#9146FF",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#8040E0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#9146FF";
              }}
            >
              Upgrade to Premium ($4.99/month)
            </button>
          ) : (
            <div
              style={{
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                border: "1px solid #f59e0b",
                borderRadius: "8px",
                padding: "16px",
                marginTop: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                <div>
                  <h4
                    style={{
                      color: "#f59e0b",
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Account Pending Approval
                  </h4>
                  <p
                    style={{
                      color: "#cbd5e0",
                      margin: 0,
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    API access and upgrades are not available until your account
                    is approved. Due to capacity limitations, your account needs
                    approval first. You will be notified via email when
                    approved.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // Premium/Admin User - Show API Token Management
        <>
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "24px",
              borderRadius: "8px",
              marginBottom: "20px",
              border: "1px solid #4a5568",
            }}
          >
            <h4
              style={{
                margin: "0 0 16px 0",
                color: "#e2e8f0",
                fontSize: "18px",
              }}
            >
              <Icon
                name="key"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              API Token Management
            </h4>
            <p
              style={{
                color: "#cbd5e0",
                marginBottom: "20px",
                fontSize: "14px",
              }}
            >
              Use this token to authenticate API requests to HAL8580 services.
            </p>

            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "16px",
                borderRadius: "6px",
                marginBottom: "16px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {apiToken ? (
                  <>
                    <code
                      style={{
                        color: "#10B981",
                        fontSize: "14px",
                        fontFamily: "monospace",
                        wordBreak: "break-all",
                        flex: 1,
                      }}
                    >
                      {showToken ? apiToken : "•".repeat(40)}
                    </code>
                    <button
                      onClick={() => setShowToken(!showToken)}
                      style={{
                        padding: "4px 8px",
                        backgroundColor: "transparent",
                        color: "#cbd5e0",
                        border: "1px solid #4a5568",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        minWidth: "50px",
                        height: "32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#4a5568";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {showToken ? "Hide" : "Show"}
                    </button>
                  </>
                ) : (
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    No API token generated yet
                  </span>
                )}
              </div>
              {apiToken && (
                <button
                  onClick={copyApiToken}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: tokenCopied ? "#10B981" : "#4a5568",
                    color: "white",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "14px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    minWidth: "80px",
                    flexShrink: 0,
                  }}
                >
                  {tokenCopied ? (
                    <>
                      <Icon
                        name="check"
                        size={14}
                        color="#10B981"
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Copied
                    </>
                  ) : (
                    "Copy"
                  )}
                </button>
              )}
            </div>

            <button
              onClick={generateApiToken}
              disabled={generatingToken}
              style={{
                padding: "10px 20px",
                backgroundColor: generatingToken ? "#4a5568" : "#9146FF",
                color: "white",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                cursor: generatingToken ? "not-allowed" : "pointer",
                transition: "all 0.2s",
              }}
            >
              {generatingToken ? (
                "Generating..."
              ) : apiToken ? (
                <>
                  <Icon
                    name="refresh-cw"
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: "6px" }}
                  />
                  Regenerate Token
                </>
              ) : (
                "Generate Token"
              )}
            </button>

            <div
              style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#FEF3C7",
                borderRadius: "6px",
                fontSize: "12px",
                color: "#92400E",
              }}
            >
              <strong>
                <Icon
                  name="alert-triangle"
                  size={14}
                  style={{ verticalAlign: "-2px", marginRight: "6px" }}
                />
                Keep your token secure!
              </strong>
              <p style={{ margin: "4px 0 0 0" }}>
                Never share your API token publicly. Regenerate it immediately
                if compromised.
              </p>
            </div>
          </div>

          {/* API Usage Guide */}
          <div
            style={{
              backgroundColor: "#2d3748",
              padding: "24px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
            }}
          >
            <h4
              style={{
                margin: "0 0 16px 0",
                color: "#e2e8f0",
                fontSize: "18px",
              }}
            >
              <Icon
                name="book-open"
                size={18}
                style={{ verticalAlign: "-3px", marginRight: "8px" }}
              />
              How to Use Your API Token
            </h4>

            <p
              style={{
                color: "#cbd5e0",
                fontSize: "14px",
                marginBottom: "20px",
                lineHeight: "1.6",
              }}
            >
              Your API token allows you to send real-time data to HAL, which he
              can use in conversations with your viewers. This includes
              information like your current song, BPM, game status, or any
              custom data you want to share.
            </p>

            <div
              style={{
                backgroundColor: "rgba(145, 70, 255, 0.1)",
                border: "1px solid rgba(145, 70, 255, 0.3)",
                padding: "16px",
                borderRadius: "8px",
                textAlign: "center",
              }}
            >
              <p
                style={{
                  color: "#9146FF",
                  margin: "0 0 12px 0",
                  fontWeight: "bold",
                }}
              >
                <Icon
                  name="bar-chart-3"
                  size={16}
                  style={{ verticalAlign: "-3px", marginRight: "6px" }}
                />
                Manage Your Real-time Data
              </p>
              <p
                style={{
                  color: "#cbd5e0",
                  fontSize: "14px",
                  margin: "0 0 16px 0",
                }}
              >
                View active data, test the API, and see full documentation in
                the Real-time Data Manager.
              </p>
              <button
                onClick={() => (window.location.href = "/dashboard/realtime")}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#9146FF",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#8040E0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#9146FF";
                }}
              >
                Go to Real-time Data Manager
                <Icon
                  name="arrow-right"
                  size={16}
                  style={{ verticalAlign: "-3px", marginLeft: "6px" }}
                />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ApiSettings;
